// Admin Settings — '/admin/settings'.
//
// Hosts the System Settings sub-cards: Live source (the frequent job,
// first), Image layer, and Embed allowlist. Each sub-card reads from the
// shared `useSystemSettings()` hook and mutates via its own mutation that
// calls `invalidate()` on success.
//
// Broadcast switches live on Overview; encoder frame rate is detected from the source.
//
// Auth gate:
//   NOT re-added — inherited from the /admin layout
//   (`apps/web/src/routes/admin/route.tsx`).
//
// API endpoints (all under `/api/system/settings/*` — NOT `/api/settings/*`,
// which does NOT exist; see PARITY.md "No nginx aliases for API paths"):
//   - GET  /api/system/settings/${vaSuffix}             → aggregate state
//   Each sub-card documents its own POST/DELETE endpoint at its definition.
//
// View-as impersonation:
//   `scopedApiPath(path, viewAsId)` threads `?view_as=<userId>` from the
//   layout's shared store through every read and write.
//
// Security / conventions:
//   - apiClient for every request; JSON preserves empty allowlist arrays.
//   - Inline error alerts render server messages directly into a
//     non-leaky `role="alert"` region; the `message` field is user-safe.
//   - Vocabulary: the composited overlay is the "image layer" everywhere
//     in copy. API field names (`watermark`) are untouched.

import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
} from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query'
import { Trash2, X } from 'lucide-react'

import { WatermarkLayoutEditor } from '@/components/watermark/WatermarkLayoutEditor'
import { Button } from '@/components/ui/button'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SectionCard } from '@/components/ui/section-card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { InlineError } from '@/components/ui'
import {
  apiClient,
  scopedApiPath,
  toFormDataPhp,
} from '@/lib/api-client'
import { describeError } from '@/lib/errors'
import { formatBytes } from '@/lib/format'
import { cn } from '@/lib/utils'
import { useViewAsStore } from '@/stores/view-as-store'
import type {
  SystemStreamKey,
  SystemEmbedOriginsBlock,
  SystemLiveSelection,
  SystemSettingMutationResponse,
  SystemSettings,
  WatermarkMutationResponse,
  WatermarkPlacement,
  WatermarkRow,
  WatermarkSettings,
  WatermarkUploadResponse,
} from '@/lib/types'

// Changing the public source can deactivate an armed flow.
const QK_FLOWS = ['flows'] as const

export const Route = createFileRoute('/admin/settings')({
  component: AdminSettingsPage,
})

const QK_SYSTEM_SETTINGS = (vaSuffix: string) =>
  ['admin', 'system-settings', vaSuffix] as const

const QK_WATERMARK = (suffix: string) =>
  ['admin', 'settings', 'watermark', suffix] as const

const MAX_IMAGE_BYTES = 2 * 1024 * 1024

function watermarkFileUrl(filename: string): string {
  // nginx serves /var/www/livevane.com/htdocs/watermarks/ at /watermarks/.
  return `/watermarks/${filename}`
}

/**
 * Normalize a user-typed allowlist entry into a canonical
 * `scheme://host[:port]` origin string — or null when the input cannot
 * be parsed as an origin. Mirrors the PHP-side normalization in
 * `api/system/settings/embed_origins/index.php` so the client-side guard
 * rejects the same shapes the server would 400 on.
 */
function normalizeEmbedOrigin(value: string): string | null {
  const raw = value.trim()
  if (raw === '') return null
  let candidate = raw
  if (!/^https?:\/\//i.test(candidate)) {
    candidate = `https://${candidate.replace(/^\/+/, '')}`
  }
  let parsed: URL
  try {
    parsed = new URL(candidate)
  } catch {
    return null
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null
  const host = parsed.hostname.toLowerCase()
  if (host === '') return null
  if (parsed.pathname !== '' && parsed.pathname !== '/') return null
  if (parsed.search !== '') return null
  if (parsed.hash !== '') return null
  const port = parsed.port !== '' ? `:${parsed.port}` : ''
  return `${parsed.protocol}//${host}${port}`
}

/** Shared aggregate-state hook for every Settings sub-card. */
function useSystemSettings(viewAsId: string | number | null) {
  const qc = useQueryClient()
  const buildPath = (base: string): string => scopedApiPath(base, viewAsId)
  const vaSuffix: string = viewAsId == null ? '' : `view_as=${String(viewAsId)}`

  const query = useQuery({
    queryKey: QK_SYSTEM_SETTINGS(vaSuffix),
    queryFn: () =>
      apiClient.get<SystemSettings>(buildPath('/system/settings/')),
  })

  const invalidate = (): Promise<void> =>
    qc.invalidateQueries({
      queryKey: ['admin', 'system-settings'],
    }) as Promise<void>

  return { query, invalidate, buildPath, vaSuffix }
}

// ── Sub-card: Embed allowlist ────────────────────────────────────────
//
// Reads from the aggregate's `embed_origins` block and writes the FULL
// `origins` array + `block_when_empty` on every POST to
// `/api/system/settings/embed_origins/` (replace, don't patch).
function EmbedAllowlistCard({
  embedOrigins,
  isLoading,
  buildPath,
  invalidate,
}: {
  embedOrigins: SystemEmbedOriginsBlock | undefined
  isLoading: boolean
  buildPath: (base: string) => string
  invalidate: () => Promise<void>
}) {
  const [originInput, setOriginInput] = useState<string>('')
  const [error, setError] = useState<string | null>(null)
  // Removing the last origin while block-when-empty is on locks every
  // embed out, so that one case confirms.
  const [pendingRemove, setPendingRemove] = useState<string | null>(null)
  const removeTriggerRef = useRef<HTMLElement | null>(null)

  const origins: ReadonlyArray<string> = embedOrigins?.origins ?? []
  const blockWhenEmpty: boolean = embedOrigins?.block_when_empty ?? false

  const mutation = useMutation({
    mutationFn: (next: {
      origins: ReadonlyArray<string>
      block_when_empty: boolean
    }) =>
      apiClient.post<SystemSettingMutationResponse>(
        buildPath('/system/settings/embed_origins/'),
        { origins: [...next.origins], block_when_empty: next.block_when_empty },
      ),
    onSuccess: () => {
      setError(null)
      setPendingRemove(null)
      return invalidate()
    },
    onError: (err) => setError(describeError(err)),
  })

  const busy = mutation.isPending || isLoading
  const trimmedInput = originInput.trim()

  const handleAdd = (): void => {
    setError(null)
    if (trimmedInput === '') {
      setError('Enter a domain or origin.')
      return
    }
    const normalized = normalizeEmbedOrigin(trimmedInput)
    if (normalized === null) {
      setError('Enter a valid http(s) origin (scheme + host, no path).')
      return
    }
    if (origins.includes(normalized)) {
      setError('That origin is already on the allowlist.')
      return
    }
    mutation.mutate(
      { origins: [...origins, normalized], block_when_empty: blockWhenEmpty },
      { onSuccess: () => setOriginInput('') },
    )
  }

  const removeOrigin = (origin: string): void => {
    mutation.mutate({ origins: origins.filter((o) => o !== origin), block_when_empty: blockWhenEmpty })
  }

  const handleRemove = (origin: string, trigger: HTMLElement): void => {
    setError(null)
    if (origins.length === 1 && blockWhenEmpty) {
      removeTriggerRef.current = trigger
      setPendingRemove(origin)
      return
    }
    removeOrigin(origin)
  }

  const handleToggleBlock = (next: boolean): void => {
    setError(null)
    mutation.mutate({ origins, block_when_empty: next })
  }

  const helperMessage = origins.length > 0
    ? 'Only the listed origins can embed the player. This switch controls what happens when the list is empty.'
    : blockWhenEmpty
      ? 'The list is empty: embeds are refused until you add a domain.'
      : 'The list is empty: any site can embed the player.'

  return (
    <>
      <SectionCard
        title="Embed allowlist"
        description="Sites allowed to embed the LiveVane player in an iframe. Add origins as scheme and host, for example https://example.com."
        data-testid="admin-settings-embed-origins"
      >
        <div className="space-y-3">
          {error !== null && pendingRemove === null && (
            <InlineError message={error} data-testid="admin-settings-embed-origins-error" />
          )}
          {isLoading && embedOrigins === undefined ? (
            <Skeleton className="h-10 w-full" />
          ) : (
            <>
              {origins.length === 0 ? (
                <p className="text-sm text-muted-foreground">No origins yet. Add one below.</p>
              ) : (
                <ul className="flex flex-wrap gap-2">
                  {origins.map((origin) => (
                    <li
                      key={origin}
                      data-testid="admin-settings-embed-origins-row"
                      className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-background/40 py-1 pl-3 pr-1 font-mono text-xs text-foreground"
                    >
                      <span>{origin}</span>
                      <button
                        type="button"
                        data-testid="admin-settings-embed-origins-remove"
                        aria-label={`Remove ${origin}`}
                        title="Remove"
                        onClick={(e) => handleRemove(origin, e.currentTarget)}
                        disabled={busy}
                        className="inline-flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <X className="size-3.5" aria-hidden="true" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              <form
                className="flex flex-wrap items-end gap-2"
                onSubmit={(e) => {
                  e.preventDefault()
                  handleAdd()
                }}
              >
                <div className="min-w-[14rem] flex-1 space-y-1.5">
                  <Label htmlFor="admin-settings-embed-origins-input">Origin to allow</Label>
                  <Input
                    id="admin-settings-embed-origins-input"
                    type="text"
                    value={originInput}
                    onChange={(e) => {
                      if (error !== null) setError(null)
                      setOriginInput(e.target.value)
                    }}
                    placeholder="https://example.com"
                    disabled={busy}
                    data-testid="admin-settings-embed-origins-input"
                  />
                </div>
                <Button
                  type="submit"
                  variant="outline"
                  data-testid="admin-settings-embed-origins-add"
                  disabled={busy || trimmedInput === ''}
                >
                  Add origin
                </Button>
              </form>

              <div className="flex items-start gap-3 pt-1">
                <Switch
                  id="admin-settings-embed-origins-block-switch"
                  data-testid="admin-settings-embed-origins-block-switch"
                  checked={blockWhenEmpty}
                  disabled={busy}
                  onCheckedChange={(next) => handleToggleBlock(next)}
                  aria-describedby="admin-settings-embed-origins-help"
                />
                <div className="min-w-0">
                  <Label htmlFor="admin-settings-embed-origins-block-switch" className="text-sm text-foreground">
                    Block embeds when the allowlist is empty
                  </Label>
                  <p id="admin-settings-embed-origins-help" className="mt-1 text-xs text-muted-foreground">
                    {helperMessage}
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      </SectionCard>

      <ConfirmDialog
        open={pendingRemove !== null}
        onOpenChange={(open) => {
          if (!open) setPendingRemove(null)
        }}
        title={pendingRemove ? `Remove ${pendingRemove}?` : ''}
        description="This is the last allowed origin and blocking when empty is enabled. Every embed stops working until you add a new origin."
        confirmLabel="Remove origin"
        busyLabel="Removing…"
        confirmVariant="destructive-solid"
        busy={mutation.isPending}
        error={pendingRemove ? error : null}
        returnFocusTo={removeTriggerRef}
        onConfirm={() => {
          if (pendingRemove !== null) removeOrigin(pendingRemove)
        }}
        data-testid="admin-settings-embed-origins-confirm"
      />
    </>
  )
}

// ── Sub-card: Live source ────────────────────────────────────────────
//
// POSTs `{id}` to `/api/system/settings/live/`. The PHP layer asserts
// ownership, calls `set_live_key`, and clears the featured-recording
// pointer as a side effect.
function LiveSourceCard({
  streamKeys,
  currentLive,
  isLoading,
  buildPath,
  invalidate,
}: {
  streamKeys: SystemStreamKey[]
  currentLive: SystemLiveSelection
  isLoading: boolean
  buildPath: (base: string) => string
  invalidate: () => Promise<void>
}) {
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const [pendingSelection, setPendingSelection] =
    useState<SystemLiveSelection>(currentLive)
  const [confirming, setConfirming] = useState(false)
  const saveRef = useRef<HTMLButtonElement | null>(null)

  // Re-sync the draft whenever the upstream cache produces a new value.
  useEffect(() => {
    setPendingSelection(currentLive)
  }, [currentLive])

  const liveValue: string =
    pendingSelection == null ? '' : String(pendingSelection)

  const streams = streamKeys.filter(key => key.enabled)

  const mutation = useMutation({
    mutationFn: (id: number) =>
      apiClient.post<SystemSettingMutationResponse>(
        buildPath('/system/settings/live/'),
        toFormDataPhp({ id }),
      ),
    onSuccess: () => {
      setError(null)
      setConfirming(false)
      return Promise.all([invalidate(), queryClient.invalidateQueries({ queryKey: QK_FLOWS })])
    },
    onError: (err) => setError(describeError(err)),
  })

  const busy = mutation.isPending || isLoading
  const hasPendingChange =
    pendingSelection !== null && pendingSelection !== currentLive
  const selectedStream = streams.find((s) => s.id === pendingSelection) ?? null
  const currentStream = streams.find((s) => s.id === currentLive) ?? null

  const handleSave = (): void => {
    if (pendingSelection === null) {
      setError('Pick a stream key first.')
      return
    }
    setError(null)
    setConfirming(true)
  }

  return (
    <>
      <SectionCard
        title="Live source"
        description="The stream key the public player shows. Switching disarms any active flow and clears any recording pinned to the front page."
        data-testid="admin-settings-live"
      >
        <div className="space-y-3">
          {error !== null && !confirming && (
            <InlineError message={error} data-testid="admin-settings-live-error" />
          )}
          {isLoading ? (
            <Skeleton className="h-10 w-full" />
          ) : streams.length === 0 ? (
            <p data-testid="admin-settings-live-empty" className="text-sm text-muted-foreground">
              No stream keys yet.{' '}
              <Link to="/admin/streams" className="text-cyber-cyan underline-offset-4 hover:underline">
                Add one on the Streams tab
              </Link>{' '}
              first.
            </p>
          ) : (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="admin-settings-live-select">Stream key shown to viewers</Label>
                <Select
                  value={liveValue}
                  disabled={busy}
                  onValueChange={(next) => {
                    if (error !== null) setError(null)
                    const id = Number(next)
                    setPendingSelection(Number.isFinite(id) ? id : null)
                  }}
                >
                  <SelectTrigger
                    id="admin-settings-live-select"
                    data-testid="admin-settings-live-select"
                    className="w-full text-foreground sm:max-w-sm"
                  >
                    <SelectValue placeholder="Choose a stream key" />
                  </SelectTrigger>
                  <SelectContent>
                    {streams.map((s) => (
                      <SelectItem
                        key={s.id}
                        value={String(s.id)}
                        data-testid="admin-settings-live-option"
                        className="text-foreground"
                      >
                        {s.name}
                        {s.live ? ' · live' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  ref={saveRef}
                  type="button"
                  size="sm"
                  data-testid="admin-settings-live-save"
                  disabled={busy || !hasPendingChange}
                  onClick={handleSave}
                >
                  {mutation.isPending ? 'Switching…' : 'Set live'}
                </Button>
                <span className="text-xs text-muted-foreground" aria-live="polite">
                  {hasPendingChange
                    ? 'Not switched yet — choose Set live to apply.'
                    : currentStream
                      ? `“${currentStream.name}” is the live source. Choose a different key to enable Set live.`
                      : 'Choose a key to enable Set live.'}
                </span>
              </div>
            </div>
          )}
        </div>
      </SectionCard>

      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={selectedStream ? `Make “${selectedStream.name}” the live source?` : 'Switch the live source?'}
        description="The public player switches to this key on its next poll. Any active flow is disarmed and any recording pinned to the front page is cleared."
        confirmLabel="Set live"
        busyLabel="Switching…"
        busy={mutation.isPending}
        error={confirming ? error : null}
        returnFocusTo={saveRef}
        onConfirm={() => {
          if (pendingSelection !== null) mutation.mutate(pendingSelection)
        }}
        data-testid="admin-settings-live-confirm"
      />
    </>
  )
}

// ── Sub-component: image layer panel ────────────────────────────────
function WatermarkPanel({
  query,
  onUpload,
  uploading,
  onSelect,
  selecting,
  onAskDelete,
  deleting,
}: {
  query: UseQueryResult<WatermarkSettings>
  onUpload: (name: string, file: File) => Promise<unknown>
  uploading: boolean
  onSelect: (items: WatermarkPlacement[]) => void
  selecting: boolean
  onAskDelete: (row: WatermarkRow, trigger: HTMLElement) => void
  deleting: boolean
}) {
  const fileRef = useRef<HTMLInputElement | null>(null)
  const [name, setName] = useState<string>('')
  const [file, setFile] = useState<File | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)
  const [items, setItems] = useState<WatermarkPlacement[]>([])
  const savedItems = JSON.stringify(query.data?.items ?? [])
  useEffect(() => { if (savedItems) setItems(JSON.parse(savedItems) as WatermarkPlacement[]) }, [savedItems])

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const next = e.target.files?.[0] ?? null
    setFileError(null)
    if (next && next.size > MAX_IMAGE_BYTES) {
      setFile(null)
      setFileError(`That file is ${formatBytes(next.size)}; the limit is 2 MB.`)
      return
    }
    setFile(next)
    if (next && name.trim() === '') {
      setName(next.name.replace(/\.[^.]+$/, ''))
    }
  }

  const handleUploadSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault()
    if (file === null || name.trim() === '' || uploading) return
    try {
      await onUpload(name.trim(), file)
    } catch {
      // Keep the selected file and name available for retry.
      return
    }
    setName('')
    setFile(null)
    if (fileRef.current) fileRef.current.value = ''
  }

  if (query.isLoading) {
    return <Skeleton className="h-32 w-full" />
  }
  if (query.isError) {
    return (
      <InlineError message={`Couldn't load the image library: ${describeError(query.error)}`} />
    )
  }

  const rows: WatermarkRow[] = query.data?.watermarks ?? []
  const layerBusy = selecting || deleting || uploading
  const saved = savedItems === JSON.stringify(items)
  const uploadDisabled = file === null || name.trim() === '' || layerBusy
  const uploadHint =
    file === null && name.trim() === ''
      ? 'Choose a file and give it a name to upload.'
      : file === null
        ? 'Choose a PNG or GIF to upload.'
        : name.trim() === ''
          ? 'Give the image a name to upload.'
          : null

  return (
    <div className="space-y-5">
      <WatermarkLayoutEditor items={items} library={rows} disabled={layerBusy} onChange={setItems} />

      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <Button disabled={layerBusy} onClick={() => onSelect(items)}>
            {selecting ? 'Applying…' : 'Apply image layer'}
          </Button>
          <Button variant="outline" disabled={layerBusy || items.length === 0} onClick={() => setItems([])}>
            Clear layer
          </Button>
          <span className="text-xs text-muted-foreground" aria-live="polite">
            {saved ? 'Layer matches what is saved.' : 'Preview only until applied.'}
          </span>
        </div>
        <p className="text-xs text-muted-foreground">
          Apply saves the layer and restarts any running encoder so viewers see it.
        </p>
      </div>

      {/* Library — add an image to the layer, or remove it from the library. */}
      <div className="space-y-2">
        <h3 className="text-sm font-medium text-foreground">Image library</h3>
        {rows.length > 0 ? (
          <ul className="space-y-2">
            {rows.map((row) => {
              const uses = items.filter((item) => item.watermark_id === row.id).length
              return (
                <li
                  key={row.id}
                  className={cn(
                    'flex flex-wrap items-center gap-3 rounded-md border bg-background/40 px-3 py-2',
                    uses > 0 ? 'border-cyber-cyan/40' : 'border-border/60',
                  )}
                >
                  <img
                    alt=""
                    src={watermarkFileUrl(row.filename)}
                    className="h-8 w-12 shrink-0 rounded border border-border/60 bg-black/40 object-contain"
                  />
                  <div className="min-w-[10rem] flex-1">
                    <p className="truncate text-sm text-foreground" title={row.name}>{row.name}</p>
                    <p className="truncate font-mono text-xs text-muted-foreground" title={row.filename}>
                      {row.filename}
                      <span className="ml-2 tabular-nums">{row.width}×{row.height}</span>
                      {uses > 0 ? <span className="ml-2 font-sans text-cyber-cyan">{uses === 1 ? 'In layer' : `In layer ×${uses}`}</span> : null}
                    </p>
                  </div>
                  <div className="ml-auto flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={layerBusy || items.length >= 8}
                      title={items.length >= 8 ? 'The layer holds up to eight images' : undefined}
                      onClick={() => setItems((prev) => [...prev, { watermark_id: row.id, x: prev.length % 2 === 0 ? 0.02 : 0.98, y: 0.98, width: 0.2, opacity: 1 }])}
                    >
                      {uses > 0 ? 'Add again' : 'Add to layer'}
                    </Button>
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="h-9 w-9 p-0"
                      disabled={layerBusy}
                      aria-label={`Remove ${row.name}`}
                      title={`Remove ${row.name} from the library`}
                      onClick={(e) => onAskDelete(row, e.currentTarget)}
                    >
                      <Trash2 aria-hidden="true" />
                    </Button>
                  </div>
                </li>
              )
            })}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">No images uploaded yet. Upload one below to add it to the layer.</p>
        )}
      </div>

      {/* Upload form — multipart, max 2 MB, PNG/GIF only (enforced server-side too). */}
      <form
        onSubmit={handleUploadSubmit}
        className="space-y-3 rounded-md border border-border/60 bg-background/40 p-4"
      >
        <div>
          <h3 className="text-sm font-medium text-foreground">Upload an image</h3>
          <p className="text-[13px] text-muted-foreground">PNG or GIF up to 2 MB. Transparency and GIF animation are kept.</p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="admin-streams-watermark-name">Name</Label>
            <Input
              id="admin-streams-watermark-name"
              type="text"
              autoComplete="off"
              value={name}
              disabled={uploading}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Channel logo"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="admin-streams-watermark-file">File</Label>
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <input
                ref={fileRef}
                id="admin-streams-watermark-file"
                type="file"
                disabled={uploading}
                accept="image/png,image/gif"
                className="sr-only"
                onChange={handleFileChange}
              />
              <Button asChild type="button" variant="outline" size="sm">
                <label htmlFor="admin-streams-watermark-file" className="cursor-pointer">
                  {file ? 'Change file' : 'Choose PNG or GIF'}
                </label>
              </Button>
              <span className="min-w-0 truncate text-sm text-muted-foreground" aria-live="polite">
                {file ? `${file.name} · ${formatBytes(file.size)}` : 'No file chosen'}
              </span>
            </div>
          </div>
        </div>
        {fileError !== null ? <InlineError message={fileError} /> : null}
        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" disabled={uploadDisabled}>
            {uploading ? 'Uploading…' : 'Upload'}
          </Button>
          {uploadHint !== null ? (
            <span className="text-xs text-muted-foreground">{uploadHint}</span>
          ) : null}
        </div>
      </form>
    </div>
  )
}

// ── Sub-card: Image layer ────────────────────────────────────────────
function WatermarkCard({
  buildPath,
  vaSuffix,
}: {
  buildPath: (base: string) => string
  vaSuffix: string
}) {
  const qc = useQueryClient()
  const [watermarkError, setWatermarkError] = useState<string | null>(null)
  const [pendingDeleteWm, setPendingDeleteWm] = useState<WatermarkRow | null>(null)
  const deleteTriggerRef = useRef<HTMLElement | null>(null)

  const watermarkQuery = useQuery({
    queryKey: QK_WATERMARK(vaSuffix),
    queryFn: () => apiClient.get<WatermarkSettings>(buildPath('/watermark/')),
  })

  const invalidateWatermark = (): Promise<void> =>
    qc.invalidateQueries({ queryKey: QK_WATERMARK(vaSuffix) }) as Promise<void>

  const uploadWatermark = useMutation({
    mutationFn: (input: { name: string; file: File }) =>
      apiClient.post<WatermarkUploadResponse>(
        buildPath('/watermark/'),
        toFormDataPhp({ action: 'upload', name: input.name, file: input.file }),
      ),
    onSuccess: () => {
      setWatermarkError(null)
      return invalidateWatermark()
    },
    onError: (err) => setWatermarkError(describeError(err)),
  })

  const selectWatermark = useMutation({
    mutationFn: (items: WatermarkPlacement[]) =>
      apiClient.post<WatermarkMutationResponse>(buildPath('/watermark/'), { action: 'compose', items }),
    onSuccess: () => {
      setWatermarkError(null)
      return invalidateWatermark()
    },
    onError: (err) => setWatermarkError(describeError(err)),
  })

  const deleteWatermark = useMutation({
    mutationFn: (id: number) =>
      apiClient.del<WatermarkMutationResponse>(
        buildPath(`/watermark/?id=${encodeURIComponent(String(id))}`),
      ),
    onSuccess: () => {
      setWatermarkError(null)
      setPendingDeleteWm(null)
      return invalidateWatermark()
    },
    onError: (err) => setWatermarkError(describeError(err)),
  })

  const placementsOf = (row: WatermarkRow): number =>
    (watermarkQuery.data?.items ?? []).filter((item) => item.watermark_id === row.id).length

  return (
    <>
      <SectionCard
        title="Image layer"
        description="Up to eight PNG or GIF images composited over the encoded video, for logos and sponsor marks."
        data-testid="admin-settings-watermark"
      >
        <div className="space-y-4">
          {watermarkError !== null && pendingDeleteWm === null && (
            <InlineError message={watermarkError} />
          )}
          <WatermarkPanel key={vaSuffix}
            query={watermarkQuery}
            onUpload={(name, file) => uploadWatermark.mutateAsync({ name, file })}
            uploading={uploadWatermark.isPending}
            onSelect={(items) => selectWatermark.mutate(items)}
            selecting={selectWatermark.isPending}
            onAskDelete={(row, trigger) => {
              deleteTriggerRef.current = trigger
              setWatermarkError(null)
              setPendingDeleteWm(row)
            }}
            deleting={deleteWatermark.isPending}
          />
        </div>
      </SectionCard>

      <ConfirmDialog
        open={pendingDeleteWm !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDeleteWm(null)
        }}
        title={pendingDeleteWm ? `Remove “${pendingDeleteWm.name}”?` : 'Remove image?'}
        description={
          pendingDeleteWm && placementsOf(pendingDeleteWm) > 0
            ? `The file is deleted and its ${placementsOf(pendingDeleteWm) === 1 ? 'placement is' : 'placements are'} removed from the saved layer; running encoders restart without it. This can’t be undone.`
            : 'The file is deleted from the library. This can’t be undone.'
        }
        confirmLabel="Remove image"
        busyLabel="Removing…"
        confirmVariant="destructive-solid"
        busy={deleteWatermark.isPending}
        error={pendingDeleteWm ? watermarkError : null}
        returnFocusTo={deleteTriggerRef}
        onConfirm={() => {
          if (pendingDeleteWm !== null) deleteWatermark.mutate(pendingDeleteWm.id)
        }}
        data-testid="admin-settings-watermark-delete-dialog"
      />
    </>
  )
}

function AdminSettingsPage() {
  const viewAsId = useViewAsStore((s) => s.viewAsId)
  const { query, invalidate, buildPath, vaSuffix } = useSystemSettings(viewAsId)

  const embedOrigins: SystemEmbedOriginsBlock | undefined = query.data?.embed_origins
  const currentLive: SystemLiveSelection =
    query.data?.live ?? query.data?.system?.live_key?.id ?? null
  const isLoading = query.isLoading || query.isError

  return (
    <div key={vaSuffix} data-testid="admin-settings" className="space-y-4">
      {query.isError && (
        <InlineError
          message="Couldn’t load system settings. Try refreshing."
          data-testid="admin-settings-load-error"
        />
      )}

      <LiveSourceCard
        streamKeys={query.data?.users ?? []}
        currentLive={currentLive}
        isLoading={isLoading}
        buildPath={buildPath}
        invalidate={invalidate}
      />
      <WatermarkCard buildPath={buildPath} vaSuffix={vaSuffix} />
      <EmbedAllowlistCard
        embedOrigins={embedOrigins}
        isLoading={isLoading}
        buildPath={buildPath}
        invalidate={invalidate}
      />
    </div>
  )
}
